import { useForm, type Resolver, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api-client";
import { applyServerValidationErrors } from "@/lib/form-errors";
import {
  CUSTOMER_SOURCES,
  CUSTOMER_SOURCE_LABELS,
} from "@/features/customer/constants";
import type {
  Customer,
  CustomerCreateRequest,
  CustomerSource,
} from "@/types/customer";

// VN-mobile-friendly pattern: allow either local 0-prefix or +84 prefix,
// followed by 9 or 10 digits. Strips whitespace before testing so users can
// paste "+84 912 345 678".
const VN_PHONE_REGEX = /^(\+84|0)\d{9,10}$/;

const customerFormSchema = z.object({
  fullName: z
    .string()
    .min(1, "Vui lòng nhập họ và tên")
    .max(200, "Họ và tên tối đa 200 ký tự"),
  phoneNumber: z
    .string()
    .min(1, "Vui lòng nhập số điện thoại")
    .refine(
      (v) => VN_PHONE_REGEX.test(v.replace(/\s+/g, "")),
      "Số điện thoại không hợp lệ",
    ),
  email: z.string().email("Email không hợp lệ").optional().or(z.literal("")),
  source: z.enum(["WALK_IN", "FACEBOOK", "ZALO", "REFERRAL", "OTHER"]),
  notes: z
    .string()
    .max(2000, "Ghi chú tối đa 2000 ký tự")
    .optional()
    .or(z.literal("")),
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

export interface CustomerFormProps {
  mode: "create" | "edit";
  initial?: Customer | undefined;
  /** Resolves to the saved customer; reject to surface a thrown ApiError. */
  onSubmit: (
    payload: CustomerCreateRequest | Partial<CustomerCreateRequest>,
  ) => Promise<Customer>;
  onCancel: () => void;
  submitLabel: string;
}

export function CustomerForm({
  mode,
  initial,
  onSubmit,
  onCancel,
  submitLabel,
}: CustomerFormProps) {
  const defaultValues: CustomerFormValues = initial
    ? {
        fullName: initial.fullName,
        phoneNumber: initial.phoneNumber,
        email: initial.email ?? "",
        source: initial.source,
        notes: initial.notes ?? "",
      }
    : {
        fullName: "",
        phoneNumber: "",
        email: "",
        source: "ZALO",
        notes: "",
      };

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema) as Resolver<CustomerFormValues>,
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const source = watch("source");

  const handle: SubmitHandler<CustomerFormValues> = async (values) => {
    const email = emptyToUndef(values.email);
    const notes = emptyToUndef(values.notes);
    // The wire type uses `?: string` (not `null`) for optional fields, so we
    // omit them rather than send null. JSON.stringify drops `undefined` keys
    // for us.
    const payload: CustomerCreateRequest = {
      fullName: values.fullName.trim(),
      phoneNumber: values.phoneNumber.replace(/\s+/g, ""),
      source: values.source,
      ...(email !== undefined ? { email } : {}),
      ...(notes !== undefined ? { notes } : {}),
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      if (error instanceof ApiError) {
        // Phone-duplicate is a 409 with code CUSTOMER_PHONE_DUPLICATE — map
        // it to an inline error on the phone field so the user can correct
        // it without scrubbing for a toast.
        if (error.body?.code === "CUSTOMER_PHONE_DUPLICATE") {
          form.setError("phoneNumber", {
            type: "server",
            message: "Số điện thoại này đã tồn tại",
          });
          return;
        }
        const handled = applyServerValidationErrors(form, error);
        if (handled) return;
      }
      // Anything else (network, unknown code) bubbles up to the caller who
      // shows a toast via `localizeError`.
      throw error;
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handle)}
      noValidate
      className="flex flex-col gap-4"
      aria-label={mode === "create" ? "Thêm khách hàng mới" : "Chỉnh sửa khách hàng"}
    >
      <div className="space-y-2">
        <Label htmlFor="fullName">Họ và tên</Label>
        <Input
          id="fullName"
          autoComplete="name"
          maxLength={200}
          {...register("fullName")}
          aria-invalid={errors.fullName ? "true" : "false"}
        />
        {errors.fullName ? (
          <p role="alert" className="text-sm text-destructive">
            {errors.fullName.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Số điện thoại</Label>
        <Input
          id="phoneNumber"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          placeholder="0912345678"
          {...register("phoneNumber")}
          aria-invalid={errors.phoneNumber ? "true" : "false"}
        />
        {errors.phoneNumber ? (
          <p role="alert" className="text-sm text-destructive">
            {errors.phoneNumber.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email (tùy chọn)</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          {...register("email")}
          aria-invalid={errors.email ? "true" : "false"}
        />
        {errors.email ? (
          <p role="alert" className="text-sm text-destructive">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="source">Nguồn</Label>
        <Select
          value={source}
          onValueChange={(v) =>
            setValue("source", v as CustomerSource, { shouldValidate: true })
          }
        >
          <SelectTrigger id="source" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CUSTOMER_SOURCES.map((s) => (
              <SelectItem key={s} value={s}>
                {CUSTOMER_SOURCE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Ghi chú (tùy chọn)</Label>
        <Textarea
          id="notes"
          rows={4}
          maxLength={2000}
          {...register("notes")}
        />
        {errors.notes ? (
          <p role="alert" className="text-sm text-destructive">
            {errors.notes.message}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function emptyToUndef(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}
